// @ts-nocheck
// KPI-strip widget bodies. Each is compact: icon + tiny label + big
// value + optional hint. Pattern mirrored from obd3-gui v1's
// KpiWidgets.tsx so the two apps share a visual language.
//
// Data comes from DashboardDataContext — dashboardStats, campaignStats,
// disconnectedSummary, agentDataObject, incomingSummaryData,
// outgoingCallsData. WidgetCard renders these in `compact` mode
// (chrome header hidden in display mode) so the tile shows only its body.
import type { LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { WIDGETS, type WidgetId } from '../config';
import { useDashboardData } from '../DashboardDataContext';
import { CountUp } from '../components/CountUp';

interface KpiTileProps {
  id: WidgetId;
  value: React.ReactNode;
  hint?: React.ReactNode;
  loading?: boolean;
  // Every KPI has a semantic tone so the icon well encodes what the metric
  // means, not just its current value:
  //   positive  – healthy / success       (emerald)
  //   warning   – needs attention         (amber)
  //   danger    – problem / breached      (rose)
  //   info      – live/real-time reading  (sky)
  //   accent    – platform / hierarchy    (violet)
  //   neutral   – plain count, no signal  (primary tint) — reserved for
  //                                        fallback; every KPI here assigns
  //                                        an explicit tone.
  tone?: 'neutral' | 'positive' | 'warning' | 'danger' | 'info' | 'accent';
  // When true, an emerald "live pulse" dot is rendered at the top-right of
  // the icon well. Used by KPIs that read real-time state (agents on-call,
  // agents online, running campaigns) — only pulses when the underlying
  // value is > 0 so tiles at rest stay quiet.
  live?: boolean;
  // When true (default for numeric values), the value tweens from its
  // previous displayed number to the new one over 800 ms.
  animate?: boolean;
  // Optional 0-100 percentage — when set, draws a progress ring around the
  // icon well. Used by rate KPIs (Agents Online %-of-total, etc.).
  percent?: number | null;
}

// Per-tone visual language. Beyond the icon well tint, a KPI's tone now
// drives:
//   `strip`   – 2px top-of-tile gradient bar, the semantic "spine" of the card
//   `glow`    – soft radial background wash so the whole tile carries a tint
//   `value`   – colour applied to the big number for positive/danger states
//               (info/accent/neutral stay on the neutral foreground so the
//               dashboard doesn't turn into a rainbow of numbers)
const TONE_STYLES: Record<
  NonNullable<KpiTileProps['tone']>,
  {
    bg: string;
    text: string;
    ring: string;
    strip: string;
    glow: string;
    value: string;
    livePulse: string;
  }
> = {
  positive: {
    bg: 'bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/60 dark:to-emerald-800/50',
    text: 'text-emerald-700 dark:text-emerald-300',
    ring: 'stroke-emerald-500',
    strip: 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500',
    glow: 'from-emerald-500/[0.05] via-transparent to-transparent',
    value: 'text-foreground',
    livePulse: 'bg-emerald-500',
  },
  warning: {
    bg: 'bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/60 dark:to-amber-800/50',
    text: 'text-amber-700 dark:text-amber-300',
    ring: 'stroke-amber-500',
    strip: 'bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500',
    glow: 'from-amber-500/[0.06] via-transparent to-transparent',
    value: 'text-foreground',
    livePulse: 'bg-amber-500',
  },
  danger: {
    bg: 'bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/60 dark:to-rose-800/50',
    text: 'text-rose-700 dark:text-rose-300',
    ring: 'stroke-rose-500',
    strip: 'bg-gradient-to-r from-rose-400 via-rose-500 to-red-500',
    glow: 'from-rose-500/[0.06] via-transparent to-transparent',
    value: 'text-rose-700 dark:text-rose-300',
    livePulse: 'bg-rose-500',
  },
  info: {
    bg: 'bg-gradient-to-br from-sky-100 to-sky-200 dark:from-sky-900/60 dark:to-sky-800/50',
    text: 'text-sky-700 dark:text-sky-300',
    ring: 'stroke-sky-500',
    strip: 'bg-gradient-to-r from-sky-400 via-sky-500 to-blue-500',
    glow: 'from-sky-500/[0.05] via-transparent to-transparent',
    value: 'text-foreground',
    livePulse: 'bg-emerald-500',
  },
  accent: {
    bg: 'bg-gradient-to-br from-violet-100 to-violet-200 dark:from-violet-900/60 dark:to-violet-800/50',
    text: 'text-violet-700 dark:text-violet-300',
    ring: 'stroke-violet-500',
    strip: 'bg-gradient-to-r from-violet-400 via-violet-500 to-fuchsia-500',
    glow: 'from-violet-500/[0.05] via-transparent to-transparent',
    value: 'text-foreground',
    livePulse: 'bg-violet-500',
  },
  neutral: {
    bg: 'bg-gradient-to-br from-primary/10 to-primary/20',
    text: 'text-primary',
    ring: 'stroke-primary',
    strip: 'bg-gradient-to-r from-primary/40 to-primary/70',
    glow: 'from-primary/[0.04] via-transparent to-transparent',
    value: 'text-foreground',
    livePulse: 'bg-emerald-500',
  },
};

interface ProgressRingProps {
  percent: number;
  className?: string;
}

/** Circular progress ring drawn around the icon well for %-style KPIs. */
function ProgressRing({ percent, className }: ProgressRingProps) {
  const r = 24;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, percent));
  const offset = c - (p / 100) * c;
  return (
    <svg
      className={cn('absolute inset-0 h-full w-full -rotate-90', className)}
      viewBox="0 0 56 56"
      aria-hidden
    >
      <circle
        cx="28"
        cy="28"
        r={r}
        fill="none"
        strokeWidth="2.5"
        className="stroke-current opacity-15"
      />
      <circle
        cx="28"
        cy="28"
        r={r}
        fill="none"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0, 0, 0.2, 1)' }}
      />
    </svg>
  );
}

function KpiTile({
  id,
  value,
  hint,
  loading,
  tone = 'neutral',
  live = false,
  animate = true,
  percent,
}: KpiTileProps) {
  const desc = WIDGETS.find((w) => w.id === id);
  if (!desc) return null;
  const Icon = desc.icon as LucideIcon;
  const styles = TONE_STYLES[tone];

  return (
    <div className="relative flex h-full min-h-[68px] flex-col overflow-hidden">
      {/* Tone strip — a 2px gradient bar hugging the top edge. Encodes the
       * KPI's semantic tone at a glance without shouting. */}
      <span aria-hidden className={cn('absolute inset-x-0 top-0 h-[2px]', styles.strip)} />

      {/* Soft wash so the whole tile carries the tone's tint without fighting
       * the neu card ground. Positioned bottom-right so the number stays
       * crisp on the top-left of the value row. */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-gradient-to-br blur-2xl',
          styles.glow,
        )}
      />

      {/* Top row — label + hint on the left, icon well on the right. */}
      <div className="relative flex items-start justify-between gap-2 pt-1">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
              {desc.title}
            </span>
            {live && (
              <span aria-hidden className="relative inline-flex">
                <span className={cn('h-1.5 w-1.5 rounded-full', styles.livePulse)} />
                <span
                  className={cn(
                    'absolute inset-0 h-1.5 w-1.5 animate-ping rounded-full opacity-75',
                    styles.livePulse,
                  )}
                />
              </span>
            )}
          </div>
          {hint && (
            <div className="mt-0.5 truncate text-[10px] text-muted-foreground/75">
              {hint}
            </div>
          )}
        </div>

        {/* Icon well — smaller footprint now that the label is the anchor.
         * Progress ring wraps it when a percent is provided. */}
        <span
          className={cn(
            'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            styles.bg,
            styles.text,
          )}
        >
          {percent != null && Number.isFinite(percent) && (
            <ProgressRing percent={percent} className={styles.ring} />
          )}
          <Icon className="relative z-10 h-[18px] w-[18px]" strokeWidth={2.2} />
        </span>
      </div>

      {/* Big number — the hero of the tile. Sits flush with the bottom so
       * two tiles side by side line up on their baselines even if one has
       * a two-line hint. */}
      <div className="relative mt-auto flex items-end gap-2">
        {loading ? (
          <Skeleton className="h-9 w-24" />
        ) : (
          <div
            className={cn(
              'text-[22px] font-bold leading-none tabular-nums tracking-[-0.02em]',
              styles.value,
            )}
          >
            {animate && typeof value === 'number' ? (
              <CountUp value={value} />
            ) : (
              value ?? '—'
            )}
          </div>
        )}
        {percent != null && Number.isFinite(percent) && !loading && (
          <span
            className={cn(
              'mb-0.5 text-[10px] font-semibold tabular-nums',
              styles.text,
            )}
          >
            {percent}%
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Normalises whatever the API returned into either a real finite number
 * (so KpiTile can hand it to <CountUp>) or null (KpiTile then renders '—').
 * Any KPI that needs a bespoke display string (e.g. "5 / 10") should still
 * build that string itself and pass it as a plain string — KpiTile will
 * render it without animation.
 */
const num = (n: number | string | undefined | null): number | null => {
  if (n === undefined || n === null || n === '') return null;
  const v = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(v) ? v : null;
};

/* ── Live-status KPIs (all non-agent roles) ─────────────────────── */

export function TotalAgentsKpi() {
  const d = useDashboardData();
  const total = num(
    d.dashboardStats?.liveStatus?.agents?.total ?? d.agentDataObject?.total_agent,
  );
  return (
    <KpiTile id="kpi.total-agents" loading={d.kpiLoading} value={total} tone="info" />
  );
}

export function AgentsOnlineKpi() {
  const d = useDashboardData();
  const online = num(d.agentDataObject?.logged_in_agent);
  const total = num(
    d.dashboardStats?.liveStatus?.agents?.total ?? d.agentDataObject?.total_agent,
  );
  const rate = online != null && total != null && total > 0
    ? Math.round((online / total) * 100)
    : null;
  return (
    <KpiTile
      id="kpi.agents-online"
      loading={d.loadingForAgentStatusDistribution}
      value={online}
      // Workforce reading, not a "positive event" — stays on the same
      // sky/info tone as Total Agents. The emerald pulse dot alone
      // indicates that some agents are actively logged in right now.
      tone="info"
      live={online != null && online > 0}
      percent={rate}
      hint={rate != null ? `${rate}% of total` : 'Logged in'}
    />
  );
}

export function AgentsOnCallKpi() {
  const d = useDashboardData();
  const v = num(d.agentDataObject?.agent_on_call);
  const total = num(
    d.dashboardStats?.liveStatus?.agents?.total ?? d.agentDataObject?.total_agent,
  );
  const rate = v != null && total != null && total > 0
    ? Math.round((v / total) * 100)
    : null;
  return (
    <KpiTile
      id="kpi.agents-on-call"
      loading={d.loadingForAgentStatusDistribution}
      value={v}
      // Live real-time indicator — sky when active, dims to info when idle.
      tone={v != null && v > 0 ? 'positive' : 'info'}
      live={v != null && v > 0}
      percent={rate}
      hint="Live"
    />
  );
}

/* ── Campaign KPIs (all non-agent roles) ────────────────────────── */

export function RunningCampaignsKpi() {
  const d = useDashboardData();
  const v = num(d.campaignStats?.runningCampaigns);
  return (
    <KpiTile
      id="kpi.running-campaigns"
      loading={d.kpiLoading}
      value={v}
      // Active work — green when there's activity, info when idle.
      tone={v != null && v > 0 ? 'positive' : 'info'}
      live={v != null && v > 0}
      hint="Right now"
    />
  );
}

export function TotalNumbersKpi() {
  const d = useDashboardData();
  return (
    <KpiTile
      id="kpi.total-numbers"
      loading={d.kpiLoading}
      value={num(d.campaignStats?.totalNos)}
      tone="accent"
    />
  );
}

export function TotalConnectedKpi() {
  const d = useDashboardData();
  return (
    <KpiTile
      id="kpi.total-connected"
      loading={d.kpiLoading}
      value={num(d.campaignStats?.totalConnected)}
      tone="positive"
    />
  );
}

/* ── Alert KPIs (CC_STAFF only) ─────────────────────────────────── */

export function LowBalanceAlertsKpi() {
  const d = useDashboardData();
  const v = num(d.disconnectedSummary?.lowBalance) ?? 0;
  return (
    <KpiTile
      id="kpi.low-balance"
      loading={d.kpiLoading}
      value={v}
      tone={v > 0 ? 'danger' : 'positive'}
      hint={v > 0 ? 'Needs top-up' : 'All clear'}
    />
  );
}

export function LicenceAlertsKpi() {
  const d = useDashboardData();
  const v = num(d.disconnectedSummary?.channelLicence) ?? 0;
  return (
    <KpiTile
      id="kpi.licence-alerts"
      loading={d.kpiLoading}
      value={v}
      tone={v > 0 ? 'warning' : 'positive'}
      hint={v > 0 ? 'Channel licence' : 'OK'}
    />
  );
}

/* ── Platform KPIs (ADMIN only) ─────────────────────────────────── */

export function ResellersKpi() {
  const d = useDashboardData();
  // Platform / hierarchy — violet.
  return (
    <KpiTile
      id="kpi.resellers"
      loading={d.kpiLoading}
      value={num(d.dashboardStats?.users?.reseller)}
      tone="accent"
    />
  );
}

export function CompaniesKpi() {
  const d = useDashboardData();
  // Platform / hierarchy — violet.
  return (
    <KpiTile
      id="kpi.companies"
      loading={d.kpiLoading}
      value={num(d.dashboardStats?.users?.companyAdmin)}
      tone="accent"
    />
  );
}
